import { useState, useEffect, useRef } from "react";

const DualRange = ({
  min = 0,
  max = 100,
  step = 1,
  value,
  onChange,
  showTooltips = false,
  format = (v) => String(v),
  className = "",
}) => {
  const [lo, hi] = value;
  const [active, setActive] = useState(null);
  const trackRef = useRef(null);

  const clamp = (n, a, b) => Math.min(Math.max(n, a), b);
  const pct = (v) => ((v - min) / (max - min)) * 100;

  const snapFromX = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    const raw = min + ratio * (max - min);
    const snapped = Math.round(raw / step) * step;
    return clamp(snapped, min, max);
  };

  const dragTo = (which, clientX) => {
    const v = snapFromX(clientX);
    if (which === "min") onChange([Math.min(v, hi), hi]);
    else onChange([lo, Math.max(v, lo)]);
  };

  useEffect(() => {
    if (!active) return;
    const onMove = (e) =>
      dragTo(active, e.clientX ?? (e.touches?.[0]?.clientX || 0));
    const onUp = () => setActive(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [active, lo, hi]);

  const onTrackPointerDown = (e) => {
    if (!trackRef.current) return;
    if (e.target?.dataset?.thumb) return;

    const v = snapFromX(e.clientX ?? (e.touches?.[0]?.clientX || 0));
    const which = Math.abs(v - lo) <= Math.abs(v - hi) ? "min" : "max";
    setActive(which);
    if (which === "min") onChange([Math.min(v, hi), hi]);
    else onChange([lo, Math.max(v, lo)]);
  };

  return (
    <div className={className}>
      <div
        ref={trackRef}
        className="relative h-1.5 rounded-full bg-[#E5E7EB] select-none"
        onPointerDown={onTrackPointerDown}
        onTouchStart={onTrackPointerDown}
      >
        <div
          className="absolute top-0 z-0 h-full rounded-full bg-[#4B5563]"
          style={{ left: `${pct(lo)}%`, width: `${pct(hi) - pct(lo)}%` }}
        />

        {/* MIN handle */}
        <button
          type="button"
          data-thumb="min"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full
                     bg-white border-2 border-[#4B5563] shadow-md cursor-pointer
                     focus:outline-none focus:ring-4 focus:ring-black/10 z-10
                     transition-transform hover:scale-105"
          style={{ left: `${pct(lo)}%`, zIndex: active === "min" ? 30 : 10 }}
          onPointerDown={(e) => {
            e.preventDefault();
            setActive("min");
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            setActive("min");
          }}
          aria-label="Minimum"
        >
          {showTooltips && (
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-2 py-1 rounded-lg border text-sm bg-white border-gray-200 text-gray-800">
              {format(lo)}
            </span>
          )}
        </button>

        {/* MAX handle */}
        <button
          type="button"
          data-thumb="max"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full
                     bg-white border-2 border-[#4B5563] shadow-md cursor-pointer
                     focus:outline-none focus:ring-4 focus:ring-black/10 z-10
                     transition-transform hover:scale-105"
          style={{ left: `${pct(hi)}%`, zIndex: active === "max" ? 30 : 10 }}
          onPointerDown={(e) => {
            e.preventDefault();
            setActive("max");
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            setActive("max");
          }}
          aria-label="Maximum"
        >
          {showTooltips && (
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-2 py-1 rounded-lg border text-sm bg-white border-gray-200 text-gray-800">
              {format(hi)}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default DualRange;
